import { PrimaryOutlineButton } from '@/components/button/button-component';
import ConfirmationModal from '@/components/confirmation-modal/confirmation-modal';
import RevertIconButton from '@/components/revert-icon-button/revert-icon-button';
import TableRowLogo from '@/components/table/components/logo/table-row-logo.component';
import ViewEditDelete from '@/components/table/components/view-edit-delete/view-edit-delete';
import RawTable from '@/components/table/table.raw';
import Tooltip from '@/components/tooltip/tooltip-component';
import { Heading3Text, Subtitle3Text } from '@/utils/global-fonts';
import { truncateLongString } from '@/utils/utils';
import parse from 'html-react-parser';
import PropTypes from 'prop-types';
import { useCallback } from 'react';
import { useFormContext } from 'react-hook-form';
import { IoMdAdd } from 'react-icons/io';
import { useParams } from 'react-router-dom';
import { useTheme } from 'styled-components';
import { getThumbnailType } from '../options-utils';
import Styled from './options-variant-all-style';

/**
 * Page that shows all Options in a table format.
 * @param {object} props - Component props
 * @param {Function} props.setIsOpen - Function to set modal open state
 * @param {Function} props.remove - Function to remove a variant
 * @param {Function} props.setEditData - Function to set edit data
 * @param {string} props.type - Type of variant
 * @returns {JSX.Element} The rendered Options Management page component.
 */
const OptionsVariantAll = ({ setIsOpen, remove, setEditData, type }) => {
  const { setValue, getValues, watch } = useFormContext();
  const { optionId } = useParams();
  const tableId = `options-list-${optionId}`;
  const theme = useTheme();

  const formVariants = watch('variant') || [];
  const data = formVariants;
  const count = formVariants.length;
  const error = null;

  // Function to revert a specific field back to original option value
  const handleRevert = useCallback(
    (variantIndex, fieldName) => {
      const currentValues = getValues();
      const { option, variant } = currentValues;

      if (!option || !variant?.[variantIndex]) {
        return;
      }

      // Get the original value from option
      const originalValue =
        fieldName === 'selectedPriceId'
          ? option.selectedPriceId
          : option[fieldName];

      // Update the variant field with original value
      setValue(`variant.${variantIndex}.${fieldName}`, originalValue);
      if (fieldName === 'selectedPriceId') {
        const optionPart = option.parts.find(
          (part) => part.price_id === option.selectedPriceId,
        );

        setValue(
          `variant.${variantIndex}.selectedPriceId`,
          optionPart?.price_id,
        );
        setValue(`variant.${variantIndex}.temp_pn`, optionPart?.temp_pn);
        setValue(
          `variant.${variantIndex}.part_number`,
          optionPart?.part_number,
        );
      }

      // Remove the field from overrides array
      const currentOverrides = variant[variantIndex].overrides || [];
      const newOverrides = currentOverrides.filter(
        (override) => override !== fieldName,
      );

      setValue(`variant.${variantIndex}.overrides`, newOverrides);
    },
    [getValues, setValue],
  );

  // Handle variant deletion - remove from array and track ID for backend deletion
  const handleDelete = useCallback(
    (rowIndex, hasQuote, hasProduct) => {
      const removeRow = () => {
        const currentValues = getValues();
        const variant = currentValues.variant?.[rowIndex];

        if (!variant?.id) {
          remove(rowIndex);
          return;
        }

        const currentRemove = currentValues.remove || [];
        const updateValues = currentValues.update || [];

        if (updateValues && updateValues.length > 0) {
          const findVariantUpdate = updateValues.find(
            (value) => value.id === variant.id,
          );
          const indexToRemove = updateValues.findIndex(
            (value) => value.id === variant.id,
          );

          if (findVariantUpdate) {
            updateValues.splice(indexToRemove, 1);
            setValue('update', updateValues);
          }
        }

        setValue('remove', [...currentRemove, variant.id]);

        // Remove from the variant array
        remove(rowIndex);
      };

      if (hasQuote || hasProduct) {
        ConfirmationModal({
          title: 'Delete Option',
          text: 'Are you sure you want to delete this option as it will be unlinked from Products?',
          onConfirm: removeRow,
          showCancelButton: true,
        });
      } else {
        removeRow();
      }
    },
    [getValues, setValue, remove],
  );

  const handleEdit = useCallback(
    (row) => {
      const parts = getValues('option.parts');
      const defaultModalOptions = {
        values: row.original,
        index: row.index,
        parts,
      };

      setEditData(defaultModalOptions);
      setIsOpen(true);
    },
    [setIsOpen, setEditData, getValues],
  );

  const handleRowDelete = useCallback(
    (row) => {
      handleDelete(row.index, row.original.hasQuote, row.original.hasProduct);
    },
    [handleDelete],
  );

  // Actions component for each table row
  const Actions = useCallback(
    ({ row }) => (
      <ViewEditDelete
        menuPlacement="top"
        feature="option"
        onView={type === 'view' ? () => handleEdit(row) : null}
        onEdit={type === 'view' ? null : () => handleEdit(row)}
        onDelete={type === 'view' ? null : () => handleRowDelete(row)}
        onDuplicate={null}
      />
    ),
    [type, handleEdit, handleRowDelete],
  );

  // Render thumbnail/logo for each row
  const renderLogoAndUniqueId = useCallback(
    ({ row }) => {
      const thumbnail = row?.original?.thumbnail;
      const key = `${row.index}-${row.original.id}`;

      const canRevert = Boolean(
        Array.isArray(row?.original?.overrides) &&
          row?.original?.overrides?.includes('thumbnail'),
      );

      if (!thumbnail && !canRevert) {
        return null;
      }

      const thumbnailUrl = getThumbnailType(thumbnail);

      return (
        <Styled.StyledRevertButtonContainer key={key}>
          {canRevert && type !== 'view' && (
            <RevertIconButton
              onClick={() => handleRevert(row.index, 'thumbnail')}
              tooltipMessage="Revert Thumbnail"
              $margin="2px !important"
            />
          )}
          {thumbnailUrl ? <TableRowLogo src={thumbnailUrl} /> : null}
        </Styled.StyledRevertButtonContainer>
      );
    },
    [handleRevert, type],
  );

  // Render part number with temp indicator
  const renderPartNumber = useCallback(
    ({ row }) => {
      const { part_number: partNumber, temp_pn: tempPn } = row?.original || {};

      const canRevert = Boolean(
        Array.isArray(row?.original?.overrides) &&
          row?.original?.overrides?.includes('selectedPriceId'),
      );

      const textContent = (
        <Subtitle3Text primary={!tempPn}>
          {truncateLongString(partNumber, 22)}
        </Subtitle3Text>
      );

      return (
        <Styled.StyledRevertButtonContainer>
          {canRevert && type !== 'view' && (
            <RevertIconButton
              onClick={() => handleRevert(row.index, 'selectedPriceId')}
              tooltipMessage="Revert Part Number"
              $margin="2px !important"
            />
          )}
          {tempPn ? (
            <Tooltip
              id={`temp-pn-${partNumber}`}
              text={textContent}
              toolTipText="Temporary Part Number"
            />
          ) : (
            <span>{textContent}</span>
          )}
        </Styled.StyledRevertButtonContainer>
      );
    },
    [handleRevert, type],
  );

  // Render text with revert icon if field can be reverted
  const renderTextWithRevertIcon = useCallback(
    (text, canRevert, fieldName, onRevert) => {
      if (!canRevert || type === 'view') {
        return <span>{parse(text)}</span>;
      }

      return (
        <Styled.StyledRevertButtonContainer>
          <RevertIconButton
            onClick={onRevert}
            tooltipMessage={`Revert ${fieldName}`}
            $margin="2px !important"
          />
          <span>{parse(text)}</span>
        </Styled.StyledRevertButtonContainer>
      );
    },
    [type],
  );

  // Generic function to render any field with revert capability
  const renderFieldWithRevert = useCallback(
    ({ row }, fieldName, displayName) => {
      const canRevert = Boolean(
        Array.isArray(row?.original?.overrides) &&
          row?.original?.overrides?.includes(fieldName),
      );

      const { [fieldName]: fieldValue = '' } = row.original || {};
      const textContent = truncateLongString(fieldValue, 22);

      const handleFieldRevert = () => {
        handleRevert(row.index, fieldName);
      };

      return (
        <span>
          {renderTextWithRevertIcon(
            textContent,
            canRevert,
            displayName,
            handleFieldRevert,
          )}
        </span>
      );
    },
    [renderTextWithRevertIcon, handleRevert],
  );

  // Field-specific render functions
  const renderName = useCallback(
    ({ row }) => renderFieldWithRevert({ row }, 'name', 'Name'),
    [renderFieldWithRevert],
  );

  const renderDescription = useCallback(
    ({ row }) => renderFieldWithRevert({ row }, 'description', 'Description'),
    [renderFieldWithRevert],
  );

  const renderQuoteDescription = useCallback(
    ({ row }) =>
      renderFieldWithRevert({ row }, 'quote_description', 'Quote Description'),
    [renderFieldWithRevert],
  );

  const renderBrochureLink = useCallback(
    ({ row }) =>
      renderFieldWithRevert({ row }, 'brochure_link', 'Brochure Link'),
    [renderFieldWithRevert],
  );

  const renderVideoLink = useCallback(
    ({ row }) => renderFieldWithRevert({ row }, 'video_link', 'Video Link'),
    [renderFieldWithRevert],
  );

  // Table column definitions
  const OptionsColumns = [
    {
      Header: 'THUMBNAIL',
      accessor: 'id',
      id: 'id',
      width: '40px',
      Cell: renderLogoAndUniqueId,
      inputSort: true,
    },
    {
      Header: 'PART NUMBER',
      id: 'part_number',
      accessor: 'part_number',
      Cell: renderPartNumber,
    },
    {
      Header: 'NAME',
      id: 'name',
      accessor: 'name',
      Cell: renderName,
    },
    {
      Header: 'FRONTEND DESCRIPTION',
      id: 'description',
      accessor: 'description',
      Cell: renderDescription,
    },
    {
      Header: 'QUOTE DESCRIPTION',
      id: 'quote_description',
      accessor: 'quote_description',
      Cell: renderQuoteDescription,
    },
    {
      Header: 'BROCHURE LINK',
      id: 'brochure_link',
      accessor: 'brochure_link',
      Cell: renderBrochureLink,
    },
    {
      Header: 'VIDEO LINK',
      id: 'video_link',
      accessor: 'video_link',
      Cell: renderVideoLink,
    },
    {
      width: '40px',
      Header: 'ACTIONS',
      id: 'actions',
      Cell: (props) => Actions(props),
    },
  ];

  return (
    <div>
      <Styled.StyledTableHeader
        $background={type === 'view' && theme.COLOR_WHITE}
      >
        <Heading3Text>Variants</Heading3Text>
        {type !== 'view' && (
          <PrimaryOutlineButton
            buttonWidth="130px"
            type="Button"
            loading={false}
            title="Add Variant"
            onClick={() => setIsOpen(true)}
            margin="0 0 0 0"
            icon={<IoMdAdd size={16} />}
          />
        )}
      </Styled.StyledTableHeader>
      <RawTable
        feature="option"
        onRowEdit={(row) => handleEdit(row)}
        onRowDelete={(row) => handleRowDelete(row)}
        columns={OptionsColumns}
        data={data ?? []}
        tableOptions={['columns']}
        id={tableId}
        defaultSort="created_at:DESC"
        dataCount={count ?? 0}
        error={error}
        loading={false}
        filters={[]}
        tableHeader={false}
        isPaginationVisible={false}
      />
    </div>
  );
};

OptionsVariantAll.propTypes = {
  setIsOpen: PropTypes.func,
  remove: PropTypes.func,
  setEditData: PropTypes.func.isRequired,
  type: PropTypes.string.isRequired,
};

OptionsVariantAll.defaultProps = {
  setIsOpen: () => {},
  remove: () => {},
};

export default OptionsVariantAll;
